import { FC } from 'react';
import * as R from 'remeda';
import { IntlShape, useIntl } from 'react-intl';
import { z, ZodType } from 'zod';
import { Schema, SchemaSection } from '@rossum/api-client/schemas';
import { Stack, SxProps, Theme, Typography } from '@rossum/ui/material';

type HeaderProps = {
  entries: {
    key: string;
    label?: string;
    value: string;
    sx?: SxProps<Theme> | undefined;
  }[];
};

type InputHeaderComponentProps<T> = {
  value: T;
  schema: Schema;
  RenderComponent: FC<HeaderProps>;
  schemaContent: SchemaSection[];
};

type OutputHeaderComponentProps = {
  value?: unknown;
  schema: Schema;
  RenderComponent: FC<HeaderProps>;
};

export const createHeader =
  <T extends ZodType>(
    Header: FC<InputHeaderComponentProps<z.TypeOf<T>>>,
    schemaType: T | ((intl: IntlShape) => T)
  ) =>
  ({
    value,
    schema,
    RenderComponent = ActionHeader,
  }: OutputHeaderComponentProps) => {
    const intl = useIntl();
    const resolvedSchema =
      typeof schemaType === 'function' ? schemaType(intl) : schemaType;
    const result = resolvedSchema.safeParse(value ?? {});

    return result.success ? (
      <Header
        value={result.data}
        schemaContent={schema.content ?? []}
        schema={schema}
        RenderComponent={RenderComponent}
      />
    ) : null;
  };

export const ActionHeader = ({
  entries,
}: {
  entries: { key: string; value: string; sx?: SxProps<Theme> }[];
}) => (
  <Stack
    direction="row"
    flexWrap="wrap"
    alignItems="center"
    columnGap={1}
    pr={16}
    divider={<span>•</span>}
  >
    {entries.map(({ key, sx, value }) =>
      value ? (
        <Typography variant="caption" key={key} sx={sx}>
          {value}
        </Typography>
      ) : null
    )}
  </Stack>
);

export const ActionTooltip = ({
  entries,
}: {
  entries: {
    key: string;
    label?: string;
    value: string;
    sx?: SxProps<Theme>;
  }[];
}) => {
  const entriesByLabel = R.pipe(
    entries,
    R.groupBy(entry => entry.label ?? ''),
    R.entries(),
    R.filter(([k, _]) => !!k)
  );

  return (
    <Stack
      direction="column"
      sx={{ textAlign: 'left', alignItems: 'flex-start' }}
    >
      {entriesByLabel.map(([label, groupedEntries]) => (
        <Stack direction="row" spacing={0.5} key={label}>
          <Typography variant="caption" fontWeight={700}>
            {label}
            {': '}
          </Typography>
          <Typography variant="caption">
            {groupedEntries.map(v => v.value).join(', ')}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};
